import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { UploadOutlined } from '@ant-design/icons';
import {
  Button,
  Form,
  Input,
  Progress,
  Typography,
  Upload,
  UploadFile,
} from 'antd';
import { RcFile, UploadChangeParam, UploadProps } from 'antd/es/upload';
import { AxiosProgressEvent } from 'axios';
import { MEDIA_ASSET_VIDEO_MIME_TYPES } from '@/entities/media-asset/config';
import { useProcContext } from '@/entities/proc';
import { useNotificationContext } from '@/features/notifications';
import { useProcManageActions } from '@/features/proc-management';
import { ProgressStatus } from '@/shared/types';
import { Col } from '@/shared/ui';
import useComponentFileUpload from './useComponentFileUpload';

const { Text } = Typography;

const getFilenameFromUrl = (url: string) => {
  const index = url.indexOf('_');
  return url.substring(index + 1);
};

export const VideoEditForm = forwardRef((_, ref) => {
  const { showNotification } = useNotificationContext();

  const {
    selectedComponent,
    selectedStep,
    setSelectedComponent,
    editFormLoading,
    setEditFormLoading,
  } = useProcContext();

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [fileForUpload, setFileForUpload] = useState<File | null>(null);
  const [importedVideoUrl, setImportedVideoUrl] = useState(
    (!selectedComponent.fileKey && selectedComponent.value) || '',
  );
  const [videoUrl, setVideoUrl] = useState(
    (selectedComponent.fileKey && selectedComponent.value) || '',
  );
  const [fileKey, setFileKey] = useState('');
  const [fileChanged, setFileChanged] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<ProgressStatus>();

  const { handleDraftCreation, updateStepComponent, updateComponentLoading } =
    useProcManageActions();
  const { uploadFile } = useComponentFileUpload();

  useImperativeHandle(
    ref,
    () => ({
      submit: onSubmit,
      isLoading: updateComponentLoading || editFormLoading,
    }),
    [
      updateComponentLoading,
      selectedComponent.id,
      videoUrl,
      fileKey,
      importedVideoUrl,
      editFormLoading,
      fileList,
    ],
  );

  const displayUploadedVideoTitle = useMemo(
    () => videoUrl && !editFormLoading && !fileChanged,
    [editFormLoading, videoUrl, fileChanged],
  );

  const beforeUpload = (file: RcFile) => {
    const supportedVideoFormats = Object.keys(MEDIA_ASSET_VIDEO_MIME_TYPES);
    const isSupportedFormat = supportedVideoFormats.includes(file.type);
    if (!isSupportedFormat) {
      showNotification({
        type: 'error',
        message: 'Error uploading video',
        description: `You can only upload ${Object.values(MEDIA_ASSET_VIDEO_MIME_TYPES).join('/').toUpperCase()} file!`,
      });
    }
    const isLt100M = file.size / 1024 / 1024 < 100;
    if (!isLt100M) {
      showNotification({
        type: 'error',
        message: 'Error uploading video',
        description: 'Video must be smaller than 100MB!',
      });
    }
    if (isLt100M && isSupportedFormat) {
      setFileList([file]);
      setFileForUpload(file);
    }
    return false;
  };

  const handleChange: UploadProps['onChange'] = (
    info: UploadChangeParam<UploadFile>,
  ) => {
    setUploadProgress(0);
    setUploadStatus(undefined);
    setFileChanged(true);
    setImportedVideoUrl('');
    if (info.file.status === 'uploading') {
      setEditFormLoading(true);
      return;
    }
    if (info.file.status === 'done') {
      const file = info.file.response;
      setEditFormLoading(false);
      setVideoUrl(file.url);
      setFileKey(file.fileKey);
      setImportedVideoUrl('');
    }
  };

  // todo: test this on staging
  const onUploadProgress = (progressEvent: AxiosProgressEvent) => {
    const { loaded, total } = progressEvent;
    const percentComplete = Math.floor((loaded * 100) / total!);
    setUploadProgress(percentComplete);
  };

  const onSubmit = async () => {
    if (fileList?.length === 0 && !importedVideoUrl) {
      setSelectedComponent(undefined);
      return;
    }

    setEditFormLoading(true);
    const isFile = Boolean(fileList?.length);
    const procId = await handleDraftCreation();
    const data = isFile
      ? await uploadFile({
          procId,
          file: fileForUpload,
          onUploadProgress,
          onUploadError: () => {
            setUploadStatus('exception');
            setEditFormLoading(false);
          },
        })
      : { value: importedVideoUrl };

    if (data) {
      updateStepComponent({
        procId,
        stepId: selectedStep?.id || '',
        data: {
          id: selectedComponent?.id,
          ...data,
          component: selectedComponent?.component,
          componentName: selectedComponent?.componentName,
        },
        onSuccess: () => setSelectedComponent(undefined),
      });
    }
  };

  useEffect(() => {
    setFileList([]);
    setFileForUpload(null);
    setFileChanged(false);
    if (selectedComponent.fileKey) {
      setVideoUrl(selectedComponent.value);
    } else {
      setImportedVideoUrl(selectedComponent.value);
    }
  }, [selectedComponent.id]);

  return (
    <Form
      data-testid="video-edit-form"
      layout="vertical"
      style={{ width: '100%' }}
    >
      <Form.Item label="Upload">
        <Col alignItems="flex-start">
          <Text
            style={{
              fontSize: 11,
              marginTop: -7,
              marginBottom: 10,
              opacity: 0.7,
            }}
          >
            Supported file types: ogg, mp4 & webm
          </Text>
          <Upload
            name="file"
            fileList={fileList}
            listType="text"
            showUploadList={true}
            maxCount={1}
            beforeUpload={beforeUpload}
            onRemove={() => {
              setFileList([]);
              setFileForUpload(null);
              setUploadProgress(0);
            }}
            onChange={handleChange}
          >
            <Button icon={<UploadOutlined />} style={{ marginTop: 10 }}>
              Select File
            </Button>
          </Upload>
        </Col>
        {Boolean(uploadProgress) && (
          <Progress
            percent={uploadProgress}
            size="small"
            status={uploadStatus}
          />
        )}
        {displayUploadedVideoTitle && (
          <div
            style={{
              fontSize: 14,
              marginTop: 40,
              marginBottom: 20,
              fontWeight: 'bold',
            }}
          >
            {getFilenameFromUrl(selectedComponent.fileKey)}
          </div>
        )}
      </Form.Item>
      <Form.Item label="Import video from URL">
        <Input
          data-testid="video-url-input"
          value={importedVideoUrl}
          onChange={(e) => {
            setImportedVideoUrl(e.target.value);
            setVideoUrl('');
            setFileList([]);
            setFileForUpload(null);
            setFileKey('');
          }}
        />
      </Form.Item>
    </Form>
  );
});

VideoEditForm.displayName = 'VideoEditForm';
